/**
 * GradientCard + PageHeader — shared visual primitives.
 *
 * Design language (v27, 2026-02-17): MINIMALIST MONOCHROME.
 *   - White surfaces with subtle slate-200 borders.
 *   - Single accent: indigo-600 (#4f46e5) for interactive / branded elements.
 *   - Slate scale for text (900 / 600 / 500 / 400).
 *   - Semantic colour reserved for status only (emerald success, amber warn,
 *     rose error, sky info) — never for decoration.
 *   - Icon tiles: bg-slate-50, text-slate-700 (neutral) — colour appears only
 *     via the optional 2px left accent bar.
 *
 * Legacy `tone` prop kept for backwards compat — instead of changing the
 * surface tint, it now only colours the left accent bar + icon highlight.
 */
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

// Accent map — tone only affects the slim left bar + icon highlight pill.
export const GRADIENT_TONES = {
  blue:    { accent: "bg-blue-500",    dot: "bg-blue-500",    label: "text-blue-700",    pill: "border-slate-200 text-blue-700 hover:bg-blue-50",    chip: "bg-blue-50 text-blue-700 border-blue-200" },
  indigo:  { accent: "bg-indigo-600",  dot: "bg-indigo-600",  label: "text-indigo-700",  pill: "border-slate-200 text-indigo-700 hover:bg-indigo-50",  chip: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  violet:  { accent: "bg-violet-500",  dot: "bg-violet-500",  label: "text-violet-700",  pill: "border-slate-200 text-violet-700 hover:bg-violet-50",  chip: "bg-violet-50 text-violet-700 border-violet-200" },
  sky:     { accent: "bg-sky-500",     dot: "bg-sky-500",     label: "text-sky-700",     pill: "border-slate-200 text-sky-700 hover:bg-sky-50",     chip: "bg-sky-50 text-sky-700 border-sky-200" },
  emerald: { accent: "bg-emerald-500", dot: "bg-emerald-500", label: "text-emerald-700", pill: "border-slate-200 text-emerald-700 hover:bg-emerald-50", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  amber:   { accent: "bg-amber-500",   dot: "bg-amber-500",   label: "text-amber-700",   pill: "border-slate-200 text-amber-700 hover:bg-amber-50",   chip: "bg-amber-50 text-amber-700 border-amber-200" },
  rose:    { accent: "bg-rose-500",    dot: "bg-rose-500",    label: "text-rose-700",    pill: "border-slate-200 text-rose-700 hover:bg-rose-50",    chip: "bg-rose-50 text-rose-700 border-rose-200" },
  pink:    { accent: "bg-pink-500",    dot: "bg-pink-500",    label: "text-pink-700",    pill: "border-slate-200 text-pink-700 hover:bg-pink-50",    chip: "bg-pink-50 text-pink-700 border-pink-200" },
  slate:   { accent: "bg-slate-500",   dot: "bg-slate-500",   label: "text-slate-700",   pill: "border-slate-200 text-slate-700 hover:bg-slate-50",   chip: "bg-slate-50 text-slate-700 border-slate-200" },
};

/** Minimal white card. */
export function GradientCard({
  tone = "indigo",
  icon: Icon,
  title,
  subtitle,
  action,
  className = "",
  bodyClassName = "",
  children,
  hover = true,
  testid,
}) {
  const t = GRADIENT_TONES[tone] || GRADIENT_TONES.indigo;
  return (
    <div
      className={`relative rounded-xl border border-slate-200 bg-white p-4 sm:p-5 transition-all duration-200 ${hover ? "hover:shadow-sm hover:border-slate-300" : ""} ${className}`}
      data-testid={testid}
    >
      {(Icon || title || action) && (
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2.5 min-w-0">
            {Icon && (
              <div className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                <Icon size={16} className={t.label} strokeWidth={2} />
              </div>
            )}
            {(title || subtitle) && (
              <div className="min-w-0">
                {title && <div className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-slate-500 truncate">{title}</div>}
                {subtitle && <div className="text-[11px] font-medium text-slate-500 truncate">{subtitle}</div>}
              </div>
            )}
          </div>
          {action}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </div>
  );
}

/** Minimal pill CTA. */
export function GradientPill({ to, onClick, tone = "indigo", children, testid }) {
  const t = GRADIENT_TONES[tone] || GRADIENT_TONES.indigo;
  const cls = `inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border text-[10.5px] font-semibold transition-all ${t.pill}`;
  if (to) return <Link to={to} className={cls} data-testid={testid}>{children} <ArrowUpRight size={11} /></Link>;
  return <button onClick={onClick} className={cls} data-testid={testid}>{children} <ArrowUpRight size={11} /></button>;
}

/** Premium page-level header — minimalist white + thin accent bar. */
import ModuleAccessButtons from "./ModuleAccessButtons";

export function PageHeader({ tone = "indigo", icon: Icon, eyebrow, title, subtitle, actions, className = "", testid, moduleKey }) {
  const t = GRADIENT_TONES[tone] || GRADIENT_TONES.indigo;
  return (
    <div
      className={`relative rounded-xl border border-slate-200 bg-white overflow-hidden ${className}`}
      data-testid={testid}
      data-page-header
    >
      {/* Thin left accent bar — the only colour on the header. */}
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${t.accent}`} />
      <div className="px-5 sm:px-6 py-4 sm:py-5 pl-6 sm:pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {Icon && (
            <div className="w-11 h-11 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
              <Icon size={20} className={t.label} strokeWidth={2} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            {eyebrow && (
              <div className="inline-flex items-center gap-1.5 mb-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
                <span className="text-[9.5px] uppercase tracking-[0.18em] font-semibold text-slate-500">{eyebrow}</span>
              </div>
            )}
            <h1 className="text-xl sm:text-2xl lg:text-[24px] font-bold tracking-tight text-slate-900 leading-tight">{title}</h1>
            {subtitle && <p className="text-slate-500 text-[12.5px] sm:text-[13px] mt-1 max-w-2xl leading-snug">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full md:w-auto">
          {moduleKey && <ModuleAccessButtons moduleKey={moduleKey} />}
          {actions}
        </div>
      </div>
    </div>
  );
}

export default GradientCard;
